import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Car, 
  Search, 
  Globe,
  DollarSign,
  Award,
  HeadphonesIcon,
  ChevronRight,
  Star
} from 'lucide-react';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import VehicleCard from './VehicleCard';
import ScrollToTop from './ScrollToTop';
import WhatsAppButton from './WhatsAppButton';
import TestimonialsSection from './TestimonialsSection';
import FAQSection from './FAQSection';
import SEOHead from './SEOHead';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const HomePage = () => {
  const navigate = useNavigate();
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [searchBrand, setSearchBrand] = useState('');
  const [searchModel, setSearchModel] = useState('');
  const [popularBrands, setPopularBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(true);

  // Fetch popular brands from API
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/public/brands`);
        setPopularBrands(response.data);
      } catch (error) {
        console.error('Error fetching brands:', error);
        // Fallback to empty array if API fails
        setPopularBrands([]);
      } finally {
        setBrandsLoading(false);
      }
    };

    fetchBrands();
  }, []);
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    fetchFeaturedVehicles();
    fetchBrands();
  }, []);

  const fetchFeaturedVehicles = async () => {
    try {
      const response = await axios.get(`${API}/public/vehicles?limit=6&sort_by=created_at&sort_order=desc`);
      setFeaturedVehicles(response.data);
      
      const filtersResponse = await axios.get(`${API}/public/filters`);
      setTotalVehicles(filtersResponse.data.total_vehicles);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/public/brands`);
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchBrand) params.append('brand', searchBrand);
    if (searchModel) params.append('model', searchModel);
    navigate(`/vehicles?${params.toString()}`);
  };

  return (
    <>
      <SEOHead 
        title="Global Trade Car - Export Quality Japanese Used Cars Worldwide"
        description={`Buy and export quality Japanese used cars. ${totalVehicles}+ vehicles in stock. Browse Toyota, Honda, Nissan, Mazda, Mitsubishi and more. Competitive prices and worldwide shipping.`}
        keywords="Japanese used cars, car export Japan, Toyota used cars, Honda export, Nissan cars, JDM vehicles, right hand drive cars, affordable used cars, Japan car dealer, vehicle export worldwide"
      />
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-15px);
          }
        }
        
        @keyframes blob {
          0%, 100% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }
        
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        @keyframes progress-line {
          0% {
            transform: scaleX(0);
            transform-origin: left;
          }
          100% {
            transform: scaleX(1);
            transform-origin: left;
          }
        }
        
        @keyframes fade-in-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
        
        .animate-progress-line {
          animation: progress-line 2s ease-out forwards;
        }
        
        .animate-fade-in-down {
          animation: fade-in-down 0.8s ease-out;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out 0.2s both;
        }
      `}</style>
      <div className="min-h-screen bg-white">
      <PublicHeader />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-red-950 to-black text-white overflow-hidden min-h-[600px]">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=85&w=1920"
            alt="Premium Japanese vehicles"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/80 via-gray-900/70 to-black/90"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(239,68,68,0.2),transparent_50%)]"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center space-y-8">
            <Badge className="bg-red-600/90 text-white border-red-500 text-sm px-5 py-2 shadow-lg">
              ✨ Premium Japanese Vehicle Exporter
            </Badge>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-tight tracking-tight">
              Luxury Japanese Vehicles
              <br />
              <span className="bg-gradient-to-r from-red-400 via-red-300 to-orange-300 bg-clip-text text-transparent">
                Delivered Worldwide
              </span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 max-w-3xl mx-auto font-light">
              Over {totalVehicles}+ premium vehicles in stock. Direct from Japan to your destination.
            </p>
            
            {/* Hero Search Bar */}
            <Card className="max-w-3xl mx-auto mt-8 shadow-2xl bg-gray-900/80 backdrop-blur-sm border border-gray-700/50">
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    placeholder="Brand (e.g., Toyota)"
                    value={searchBrand}
                    onChange={(e) => setSearchBrand(e.target.value)}
                    className="hero-search-input text-base border-gray-600 focus:border-red-500 focus:ring-red-500"
                  />
                  <Input
                    placeholder="Model (e.g., Land Cruiser)"
                    value={searchModel}
                    onChange={(e) => setSearchModel(e.target.value)}
                    className="hero-search-input text-base border-gray-600 focus:border-red-500 focus:ring-red-500"
                  />
                  <Button 
                    onClick={handleSearch}
                    className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white h-11 shadow-lg"
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Search Vehicles
                  </Button>
                </div>
                <div className="mt-4 text-center">
                  <Button 
                    variant="link" 
                    onClick={() => navigate('/vehicles')}
                    className="text-red-600 hover:text-red-700 font-semibold"
                  >
                    Advanced Search <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mt-12">
              <div>
                <div className="text-3xl font-bold">{totalVehicles}+</div>
                <div className="text-sm text-red-200">Vehicles Available</div>
              </div>
              <div>
                <div className="text-3xl font-bold">7,000+</div>
                <div className="text-sm text-red-200">Happy Customers</div>
              </div>
              <div>
                <div className="text-3xl font-bold">50+</div>
                <div className="text-sm text-red-200">Countries Shipped</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Popular Brands */}
      <div className="relative bg-gray-900 py-16 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1758578442773-35c1333be335?w=1920&q=85"
            alt="Luxury vehicles"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/95 via-gray-900/90 to-gray-900/95"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-10 text-white">Popular Brands</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
            {brands.map((brand) => (
              <Button
                key={brand.name}
                className="h-20 bg-white/95 backdrop-blur-sm border-2 border-white/30 text-gray-800 hover:bg-red-50 hover:border-red-500 hover:text-red-600 transition-all group"
                onClick={() => navigate(`/vehicles?brand=${brand.name}`)}
              >
                <div className="flex flex-col items-center justify-center gap-2">
                  <img 
                    src={brand.logo_url} 
                    alt={brand.name}
                    className="w-auto object-contain group-hover:scale-110 transition-transform"
                    style={{
                      height: `${32 * (brand.size_multiplier || 1)}px`,
                      maxHeight: '48px'
                    }}
                  />
                  <span className="font-semibold text-xs">{brand.name}</span>
                </div>
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Featured Vehicles */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">New Arrivals</h2>
              <p className="text-gray-600 mt-2">Latest additions to our inventory</p>
            </div>
            <Button 
              onClick={() => navigate('/vehicles')}
              variant="outline"
              className="hidden sm:flex"
            >
              View All Vehicles <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="animate-pulse">
                  <div className="h-48 bg-gray-200"></div>
                  <CardContent className="p-6">
                    <div className="h-6 bg-gray-200 rounded mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredVehicles.map((vehicle) => (
                <VehicleCard 
                  key={vehicle.id} 
                  vehicle={vehicle} 
                />
              ))}
            </div>
          )}

          <div className="text-center mt-8 sm:hidden">
            <Button onClick={() => navigate('/vehicles')}>
              View All Vehicles <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Why Buy From Us */}
      <div className="relative bg-gray-800 py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&q=85"
            alt="Luxury cars"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-gray-800/85 to-black/90"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white">Why Choose Us?</h2>
            <p className="text-gray-300 mt-2">Your trusted partner for Japanese vehicles</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600 text-white rounded-full mb-4">
                <Award className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-white">Japanese Quality</h3>
              <p className="text-sm text-gray-300">
                &ldquo;Made in Japan&rdquo; means superior technology, reliability, and trustworthiness.
              </p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600 text-white rounded-full mb-4">
                <DollarSign className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-white">Competitive Pricing</h3>
              <p className="text-sm text-gray-300">
                Direct from auctions with no middlemen. Best prices for quality vehicles.
              </p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600 text-white rounded-full mb-4">
                <Globe className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-white">Worldwide Shipping</h3>
              <p className="text-sm text-gray-300">
                Fast and secure delivery to over 50 countries. We handle all logistics.
              </p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600 text-white rounded-full mb-4">
                <HeadphonesIcon className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-white">Expert Support</h3>
              <p className="text-sm text-gray-300">
                Dedicated team to assist you through every step of your purchase.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* How to Buy */}
      <div className="relative py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
        {/* Animated background circles */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-72 h-72 bg-red-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-pink-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-20 left-1/2 w-72 h-72 bg-red-50 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 animate-fade-in-down">How to Buy</h2>
            <p className="text-gray-600 text-lg animate-fade-in-up">Your journey to owning a premium Japanese vehicle in 4 simple steps</p>
          </div>
          
          <div className="relative">
            {/* Connecting line - desktop only */}
            <div className="hidden md:block absolute top-24 left-0 right-0 h-1">
              <div className="h-full bg-gradient-to-r from-transparent via-red-600 to-transparent animate-progress-line"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
              {/* Step 1 */}
              <div className="relative group animate-float" style={{animationDelay: '0s'}}>
                <div className="relative">
                  <div className="relative w-32 h-32 mx-auto mb-6 transform transition-all duration-500 group-hover:scale-110">
                    {/* Outer ring */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-400 to-red-600 animate-pulse-slow"></div>
                    {/* Middle ring */}
                    <div className="absolute inset-2 rounded-full bg-gradient-to-br from-red-500 to-red-700"></div>
                    {/* Inner circle */}
                    <div className="absolute inset-4 rounded-full bg-white flex items-center justify-center shadow-xl">
                      <span className="text-4xl font-extrabold bg-gradient-to-br from-red-600 to-red-800 bg-clip-text text-transparent">1</span>
                    </div>
                    {/* Sparkle effect */}
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full animate-ping"></div>
                  </div>
                  <div className="text-center transform transition-all duration-300 group-hover:-translate-y-2">
                    <h3 className="font-bold text-xl mb-3 text-gray-900 group-hover:text-red-600 transition-colors">Choose Your Car</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">Browse our premium inventory of over 10+ quality Japanese vehicles</p>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative group animate-float" style={{animationDelay: '0.2s'}}>
                <div className="relative">
                  <div className="relative w-32 h-32 mx-auto mb-6 transform transition-all duration-500 group-hover:scale-110">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-400 to-red-600 animate-pulse-slow" style={{animationDelay: '0.2s'}}></div>
                    <div className="absolute inset-2 rounded-full bg-gradient-to-br from-red-500 to-red-700"></div>
                    <div className="absolute inset-4 rounded-full bg-white flex items-center justify-center shadow-xl">
                      <span className="text-4xl font-extrabold bg-gradient-to-br from-red-600 to-red-800 bg-clip-text text-transparent">2</span>
                    </div>
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full animate-ping" style={{animationDelay: '0.2s'}}></div>
                  </div>
                  <div className="text-center transform transition-all duration-300 group-hover:-translate-y-2">
                    <h3 className="font-bold text-xl mb-3 text-gray-900 group-hover:text-red-600 transition-colors">Get in Touch</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">Send us your requirements and our experts will assist you</p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative group animate-float" style={{animationDelay: '0.4s'}}>
                <div className="relative">
                  <div className="relative w-32 h-32 mx-auto mb-6 transform transition-all duration-500 group-hover:scale-110">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-400 to-red-600 animate-pulse-slow" style={{animationDelay: '0.4s'}}></div>
                    <div className="absolute inset-2 rounded-full bg-gradient-to-br from-red-500 to-red-700"></div>
                    <div className="absolute inset-4 rounded-full bg-white flex items-center justify-center shadow-xl">
                      <span className="text-4xl font-extrabold bg-gradient-to-br from-red-600 to-red-800 bg-clip-text text-transparent">3</span>
                    </div>
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full animate-ping" style={{animationDelay: '0.4s'}}></div>
                  </div>
                  <div className="text-center transform transition-all duration-300 group-hover:-translate-y-2">
                    <h3 className="font-bold text-xl mb-3 text-gray-900 group-hover:text-red-600 transition-colors">Secure Payment</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">Complete payment securely through multiple payment options</p>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="relative group animate-float" style={{animationDelay: '0.6s'}}>
                <div className="relative">
                  <div className="relative w-32 h-32 mx-auto mb-6 transform transition-all duration-500 group-hover:scale-110">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-400 to-red-600 animate-pulse-slow" style={{animationDelay: '0.6s'}}></div>
                    <div className="absolute inset-2 rounded-full bg-gradient-to-br from-red-500 to-red-700"></div>
                    <div className="absolute inset-4 rounded-full bg-white flex items-center justify-center shadow-xl">
                      <span className="text-4xl font-extrabold bg-gradient-to-br from-red-600 to-red-800 bg-clip-text text-transparent">4</span>
                    </div>
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full animate-ping" style={{animationDelay: '0.6s'}}></div>
                  </div>
                  <div className="text-center transform transition-all duration-300 group-hover:-translate-y-2">
                    <h3 className="font-bold text-xl mb-3 text-gray-900 group-hover:text-red-600 transition-colors">Receive Your Car</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">We handle worldwide shipping and deliver to your destination</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Find Your Perfect Vehicle?
          </h2>
          <p className="text-xl text-red-100 mb-8">
            Browse our complete inventory or contact us for assistance
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/vehicles')}
              className="bg-white text-red-600 hover:bg-gray-100"
            >
              Browse All Vehicles
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/contact')}
              className="border-white text-white hover:bg-red-700"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </div>

      <PublicFooter />
      <ScrollToTop />
      <WhatsAppButton />
      </div>
    </>
  );
};

export default HomePage;